// The Tapestry postcard (§9.8, §23.8). During PLAY of each scoring round of the match it samples
// every bean in play every 6 ticks from the live FrameViews, takes splash points from live KO
// events and the map silhouette from `groundAt` on a 0.1 m grid at round start. `render` draws the
// 1080 × 1080 Canvas 2D card. Replayed events and replay frames are ignored. WP7 owns it after WP0;
// WP9's Tapestry modal calls `render`.
import { MAX_BEANS, NO_SLOT, VOID } from '@shared/constants';
import { COLORBLIND_IDENTITY_HEX, IDENTITIES } from '@shared/cosmetics';
import { EV } from '@shared/events';
import { ModeId, RoomPhase, type MapId } from '@shared/ids';
import { MAPS } from '@shared/maps/index';
import type { RoundInitDTO } from '@shared/protocol/messages';
import { createWorld } from '@shared/sim/world';
import { onSim, type Bus } from '../core/bus';
import { MAP_PALETTES } from '../render/maps/palettes';
import type { FrameView } from './FrameView';

export const TAPESTRY = {
  size: 1080,
  sampleEveryTicks: 6,
  gridM: 0.1,
  /** Ground sampled this far beyond the map bounds. */
  marginM: 1,
  /** Cells on the ground's edge are supersampled n × n for antialiased coverage. */
  edgeSupersample: 4,
  lineWidth: 4,
  /** A bean that moves further than this between two samples (a respawn) starts a new line. */
  breakDistM: 3,
  /** Width of the accent rim band around the ground's edges, in grid cells (0.1 m). */
  rimCells: 4,
} as const;

/** What the card prints besides the recorded round. */
export interface TapestryInfo {
  code: string;
  date: Date;
  /** Match standings, best first; the top 3 are printed. */
  standings: readonly { name: string; score: number; identity: number }[];
  /** "Colorblind: on" identity palette (§12.2). */
  colorblind?: boolean;
}

export interface TapestryOptions {
  /** Canvas factory; defaults to `document.createElement('canvas')`. */
  createCanvas?(width: number, height: number): HTMLCanvasElement;
}

interface Area { minX: number; minY: number; cols: number; rows: number }

interface RoundRecord {
  roundIndex: number;
  mapId: MapId;
  golden: boolean;
  area: Area;
  /** Ground coverage 0–255 per cell at round start; row-major from (minX, minY). */
  mask: Uint8Array;
  /** Per slot: x, y pairs; a NaN pair starts a new polyline. */
  paths: number[][];
  /** Per slot: identity seen in the samples, -1 if never sampled. */
  identity: number[];
  /** Per slot: the current polyline continues at the next sample. */
  open: boolean[];
  splashes: { x: number; y: number }[];
  winner: number;
  /** Index (round tick / 6) of the last sample taken. */
  lastSample: number;
}

const INK = '#2A1A3A';
const CREAM = '#FFF6E9';
const STAR_PRINT = 'rgba(255, 201, 60, 0.22)';
const CHIP = '#FFFFFF';
const GOLD = '#FFD24A';
const GOLD_DEEP = '#E8B64C';
const PINK = '#FF4F9A';
const MEDALS = ['#FFD24A', '#CFD8E3', '#E0915A'] as const;
const PLACES = ['1ST', '2ND', '3RD'] as const;
const HALO = { alpha: 0.3, extra: 3 } as const;
const LAYOUT = {
  frameInset: 22, frameWidth: 8, frameRadius: 40,
  titleY: 100, subtitleY: 146,
  /** The map window: the arena's sky behind its silhouette; paths, splashes and the crown are clipped to it. */
  map: { left: 64, top: 176, width: 952, height: 700, radius: 34, border: 6, shadow: 10 },
  /** Ground bounds are framed with this much extra room (m) so splashes off the rim stay visible. */
  viewMarginM: 2.2,
  standingsY: 948, chip: { width: 300, height: 92, radius: 22, gap: 26 },
  shadowOffset: 8,
  splashArm: 13, crownWidth: 50,
  stars: 46,
} as const;

function defaultCanvas(width: number, height: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  return c;
}

function context(c: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('Tapestry: Canvas 2D is unavailable');
  return ctx;
}

function hexRgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function isScoring(modeId: ModeId): boolean {
  return modeId !== ModeId.LOBBY && modeId !== ModeId.PODIUM;
}

export class Tapestry {
  private readonly rounds = new Map<number, RoundRecord>();
  private current: { roundId: number; rec: RoundRecord } | null = null;
  private readonly createCanvas: (width: number, height: number) => HTMLCanvasElement;
  private readonly unsubscribe: (() => void)[];

  constructor(bus: Bus, opts: TapestryOptions = {}) {
    this.createCanvas = opts.createCanvas ?? defaultCanvas;
    // Verification handle for the WP7 browser check (render timing, the PNG itself).
    if (typeof window !== 'undefined' && !opts.createCanvas) (window as Window & { __SHOVEOFF_TAPESTRY__?: Tapestry }).__SHOVEOFF_TAPESTRY__ = this;
    this.unsubscribe = [
      bus.on('round:init', (init) => this.beginRound(init)),
      bus.on('round:end', (end) => {
        const rec = this.rounds.get(end.roundId);
        if (rec) rec.winner = end.winner;
      }),
      bus.on('room', (room) => {
        if (room.phase === RoomPhase.LOBBY) this.clear();
      }),
      onSim(bus, (ev, _local, replay) => {
        if (!replay && ev.type === EV.KO && this.current) this.current.rec.splashes.push({ x: ev.x, y: ev.y });
      }),
    ];
  }

  /** Round ids with a recording (the match's scoring rounds so far). */
  get recorded(): number[] {
    return [...this.rounds.keys()];
  }

  /** Samples the live view; call once per frame with the online source's view. */
  sample(view: FrameView): void {
    const cur = this.current;
    if (!cur || view.source !== 'online' || view.roundId !== cur.roundId || view.roomPhase !== RoomPhase.PLAY) return;
    const rec = cur.rec;
    const k = Math.floor(view.roundTick / TAPESTRY.sampleEveryTicks);
    if (k <= rec.lastSample) return;
    rec.lastSample = k;
    for (let slot = 0; slot < MAX_BEANS; slot++) {
      const b = view.beans[slot];
      if (!b.active || b.out || b.ghost) {
        rec.open[slot] = false;
        continue;
      }
      const path = rec.paths[slot];
      const n = path.length;
      if (rec.open[slot] && n >= 2 && Math.hypot(b.x - path[n - 2], b.y - path[n - 1]) > TAPESTRY.breakDistM) rec.open[slot] = false;
      if (!rec.open[slot] && n > 0) path.push(Number.NaN, Number.NaN);
      path.push(b.x, b.y);
      rec.open[slot] = true;
      rec.identity[slot] = b.identity;
    }
  }

  /** Draws the postcard of a recorded round. Throws RangeError for a round without a recording. */
  render(roundId: number, info: TapestryInfo): HTMLCanvasElement {
    const rec = this.rounds.get(roundId);
    if (!rec) throw new RangeError(`Tapestry: round ${roundId} was not recorded`);
    const size = TAPESTRY.size;
    const canvas = this.createCanvas(size, size);
    const ctx = context(canvas);
    const colors = info.colorblind ? COLORBLIND_IDENTITY_HEX : IDENTITIES.map((i) => i.hex);

    drawBackground(ctx, rec.roundIndex * 31 + rec.mapId);
    const map = LAYOUT.map;
    const pal = MAP_PALETTES[rec.mapId];
    drawMapWindow(ctx, pal.skyTop, pal.skyBottom);
    // Frame the ground bounds plus a margin (splashes land just off the rim), centered in the window.
    const g = TAPESTRY.gridM;
    const vm = LAYOUT.viewMarginM / g;
    const scale = Math.min((map.width - 2 * map.border) / (rec.area.cols + 2 * vm), (map.height - 2 * map.border) / (rec.area.rows + 2 * vm));
    const ox = map.left + (map.width - rec.area.cols * scale) / 2;
    const oy = map.top + (map.height - rec.area.rows * scale) / 2;
    const px = (x: number): number => ox + ((x - rec.area.minX) / g) * scale;
    const py = (y: number): number => oy + ((y - rec.area.minY) / g) * scale;

    ctx.save();
    roundRect(ctx, map.left + map.border / 2, map.top + map.border / 2, map.width - map.border, map.height - map.border, map.radius - map.border / 2);
    ctx.clip();
    this.drawSilhouette(ctx, rec, ox, oy, scale);
    drawPaths(ctx, rec, colors, px, py);
    // Splash marks stay fully inside the window (a long fling can land past its edge).
    const arm = LAYOUT.splashArm + 6;
    for (const s of rec.splashes) {
      drawSplash(ctx, Math.min(map.left + map.width - arm, Math.max(map.left + arm, px(s.x))), Math.min(map.top + map.height - arm, Math.max(map.top + arm, py(s.y))));
    }
    const w = rec.winner;
    // The crown stands where the winner last stood on the ground (not where a late fling left it),
    // and always fully inside the window.
    const wp = w !== NO_SLOT && w < MAX_BEANS ? lastGroundPoint(rec, rec.paths[w]) ?? lastPoint(rec.paths[w]) : null;
    if (wp) {
      const cw = LAYOUT.crownWidth;
      const cx = Math.min(map.left + map.width - cw, Math.max(map.left + cw, px(wp[0])));
      const cy = Math.min(map.top + map.height - 24, Math.max(map.top + cw + 12, py(wp[1])));
      drawCrown(ctx, cx, cy);
    }
    ctx.restore();
    strokeMapWindow(ctx);
    drawText(ctx, rec, info, colors);
    return canvas;
  }

  /** Forgets the match (back in the lobby). */
  clear(): void {
    this.rounds.clear();
    this.current = null;
  }

  dispose(): void {
    for (const off of this.unsubscribe) off();
    this.unsubscribe.length = 0;
  }

  private beginRound(init: RoundInitDTO): void {
    const cfg = init.cfg;
    if (!isScoring(cfg.modeId)) {
      this.current = null;
      return;
    }
    const existing = this.rounds.get(init.roundId);
    if (existing) {
      // A reconnect repeats `round`: keep recording into the same card.
      this.current = { roundId: init.roundId, rec: existing };
      return;
    }
    const def = MAPS[cfg.mapId];
    const world = createWorld(cfg, init.roundId, false);
    const m = TAPESTRY.marginM;
    const g = TAPESTRY.gridM;
    const area: Area = {
      minX: def.bounds.minX - m, minY: def.bounds.minY - m,
      cols: Math.ceil((def.bounds.maxX - def.bounds.minX + 2 * m) / g),
      rows: Math.ceil((def.bounds.maxY - def.bounds.minY + 2 * m) / g),
    };
    const mask = groundCoverage((x, y) => def.groundAt(world, x, y) > VOID / 2, area);
    const rec: RoundRecord = {
      roundIndex: init.roundIndex, mapId: cfg.mapId, golden: cfg.golden, area, mask,
      paths: Array.from({ length: MAX_BEANS }, () => []),
      identity: new Array<number>(MAX_BEANS).fill(-1),
      open: new Array<boolean>(MAX_BEANS).fill(false),
      splashes: [], winner: NO_SLOT, lastSample: Number.NEGATIVE_INFINITY,
    };
    this.rounds.set(init.roundId, rec);
    this.current = { roundId: init.roundId, rec };
  }

  /**
   * The ground as a sticker: the map's base color inside an ink outline with an ink drop shadow.
   * One small canvas holds the coverage twice (ink grown by a cell above, base color below); each
   * half is scaled onto the card.
   */
  private drawSilhouette(ctx: CanvasRenderingContext2D, rec: RoundRecord, ox: number, oy: number, scale: number): void {
    const { cols, rows } = rec.area;
    const tile = this.createCanvas(cols, 3 * rows);
    const tctx = context(tile);
    const img = tctx.createImageData(cols, 3 * rows);
    // Ink outline, then the map's first accent as a rim band, then the base color inset by the band
    // (the saucer's mint rim, the table's neon edge, the cake's cream piping…).
    const pal = MAP_PALETTES[rec.mapId];
    const inner = shrink(rec.mask, cols, rows, TAPESTRY.rimCells);
    const layers: [Uint8Array, string][] = [[grow(rec.mask, cols, rows), INK], [rec.mask, pal.accent1], [inner, pal.base]];
    layers.forEach(([alpha, hex], layer) => {
      const [r, g, b] = hexRgb(hex);
      const offset = layer * alpha.length * 4;
      for (let i = 0; i < alpha.length; i++) {
        if (alpha[i] === 0) continue;
        const k = offset + i * 4;
        img.data[k] = r;
        img.data[k + 1] = g;
        img.data[k + 2] = b;
        img.data[k + 3] = alpha[i];
      }
    });
    tctx.putImageData(img, 0, 0);
    const w = cols * scale;
    const h = rows * scale;
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(tile, 0, 0, cols, rows, ox, oy + LAYOUT.shadowOffset, w, h);
    ctx.drawImage(tile, 0, 0, cols, rows, ox, oy, w, h);
    ctx.drawImage(tile, 0, rows, cols, rows, ox, oy, w, h);
    ctx.drawImage(tile, 0, 2 * rows, cols, rows, ox, oy, w, h);
    ctx.restore();
  }
}

/**
 * Ground coverage (0–255) of each grid cell: its center sample decides, and cells whose neighbors
 * disagree are supersampled so the scaled-up silhouette has smooth edges.
 */
function groundCoverage(hasGround: (x: number, y: number) => boolean, area: Area): Uint8Array {
  const { cols, rows, minX, minY } = area;
  const g = TAPESTRY.gridM;
  const out = new Uint8Array(cols * rows);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) if (hasGround(minX + (c + 0.5) * g, minY + (r + 0.5) * g)) out[r * cols + c] = 255;
  }
  const center = out.slice();
  const n = TAPESTRY.edgeSupersample;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const v = center[r * cols + c];
      const edge = (c > 0 && center[r * cols + c - 1] !== v) || (c + 1 < cols && center[r * cols + c + 1] !== v)
        || (r > 0 && center[(r - 1) * cols + c] !== v) || (r + 1 < rows && center[(r + 1) * cols + c] !== v);
      if (!edge) continue;
      let hits = 0;
      for (let j = 0; j < n; j++) {
        for (let i = 0; i < n; i++) if (hasGround(minX + (c + (i + 0.5) / n) * g, minY + (r + (j + 0.5) / n) * g)) hits++;
      }
      out[r * cols + c] = Math.round((hits / (n * n)) * 255);
    }
  }
  return out;
}

/** `alpha` shrunk by `k` cells (a separable (2k+1)² minimum): the base color inside the rim band. */
function shrink(alpha: Uint8Array, cols: number, rows: number, k: number): Uint8Array {
  const tmp = new Uint8Array(alpha.length);
  const out = new Uint8Array(alpha.length);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      let m = 255;
      for (let d = -k; d <= k && m > 0; d++) {
        const cc = c + d;
        m = cc < 0 || cc >= cols ? 0 : Math.min(m, alpha[r * cols + cc]);
      }
      tmp[r * cols + c] = m;
    }
  }
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      let m = 255;
      for (let d = -k; d <= k && m > 0; d++) {
        const rr = r + d;
        m = rr < 0 || rr >= rows ? 0 : Math.min(m, tmp[rr * cols + c]);
      }
      out[r * cols + c] = m;
    }
  }
  return out;
}

/** `alpha` grown by one cell (3 × 3 maximum): the ink outline under the base color. */
function grow(alpha: Uint8Array, cols: number, rows: number): Uint8Array {
  const out = new Uint8Array(alpha.length);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      let m = 0;
      for (let dr = -1; dr <= 1; dr++) {
        const rr = r + dr;
        if (rr < 0 || rr >= rows) continue;
        for (let dc = -1; dc <= 1; dc++) {
          const cc = c + dc;
          if (cc >= 0 && cc < cols) m = Math.max(m, alpha[rr * cols + cc]);
        }
      }
      out[r * cols + c] = m;
    }
  }
  return out;
}

/** The last sample of `path` over ground (by the round-start coverage mask). */
function lastGroundPoint(rec: RoundRecord, path: readonly number[]): [number, number] | null {
  const { minX, minY, cols, rows } = rec.area;
  const g = TAPESTRY.gridM;
  for (let i = path.length - 2; i >= 0; i -= 2) {
    if (Number.isNaN(path[i])) continue;
    const c = Math.floor((path[i] - minX) / g);
    const r = Math.floor((path[i + 1] - minY) / g);
    if (c >= 0 && r >= 0 && c < cols && r < rows && rec.mask[r * cols + c] >= 128) return [path[i], path[i + 1]];
  }
  return null;
}

function lastPoint(path: readonly number[]): [number, number] | null {
  for (let i = path.length - 2; i >= 0; i -= 2) if (!Number.isNaN(path[i])) return [path[i], path[i + 1]];
  return null;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

/** Small deterministic hash for the printed star pattern. */
function hash01(i: number, salt: number): number {
  const v = Math.sin(i * 127.1 + salt * 311.7) * 43758.5453;
  return v - Math.floor(v);
}

/** A four-point sparkle path centered on (x, y). */
function sparkle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  for (let i = 0; i < 8; i++) {
    const a = (i * Math.PI) / 4 - Math.PI / 2;
    const rr = i % 2 === 0 ? r : r * 0.28;
    if (i === 0) ctx.moveTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
    else ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
  }
  ctx.closePath();
}

/** The postcard stock: cream, a faint printed star pattern, an ink frame. */
function drawBackground(ctx: CanvasRenderingContext2D, seed: number): void {
  const s = TAPESTRY.size;
  const f = LAYOUT.frameInset;
  ctx.fillStyle = CREAM;
  ctx.fillRect(0, 0, s, s);
  ctx.save();
  ctx.fillStyle = STAR_PRINT;
  for (let i = 0; i < LAYOUT.stars; i++) {
    const x = f + 20 + hash01(i, seed) * (s - 2 * f - 40);
    const y = f + 20 + hash01(i, seed + 7) * (s - 2 * f - 40);
    sparkle(ctx, x, y, 7 + hash01(i, seed + 13) * 9);
    ctx.fill();
  }
  ctx.restore();
  ctx.strokeStyle = INK;
  ctx.lineWidth = LAYOUT.frameWidth;
  roundRect(ctx, f, f, s - 2 * f, s - 2 * f, LAYOUT.frameRadius);
  ctx.stroke();
}

/** The map window: a hard sticker shadow and the arena's sky gradient. */
function drawMapWindow(ctx: CanvasRenderingContext2D, top: string, bottom: string): void {
  const m = LAYOUT.map;
  ctx.save();
  ctx.fillStyle = INK;
  roundRect(ctx, m.left, m.top + m.shadow, m.width, m.height, m.radius);
  ctx.fill();
  const grad = ctx.createLinearGradient(0, m.top, 0, m.top + m.height);
  grad.addColorStop(0, top);
  grad.addColorStop(1, bottom);
  ctx.fillStyle = grad;
  roundRect(ctx, m.left, m.top, m.width, m.height, m.radius);
  ctx.fill();
  ctx.restore();
}

function strokeMapWindow(ctx: CanvasRenderingContext2D): void {
  const m = LAYOUT.map;
  ctx.save();
  ctx.strokeStyle = INK;
  ctx.lineWidth = m.border;
  roundRect(ctx, m.left, m.top, m.width, m.height, m.radius);
  ctx.stroke();
  ctx.restore();
}

function drawPaths(ctx: CanvasRenderingContext2D, rec: RoundRecord, colors: readonly string[], px: (x: number) => number, py: (y: number) => number): void {
  ctx.save();
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  for (let slot = 0; slot < MAX_BEANS; slot++) {
    const path = rec.paths[slot];
    if (path.length < 2 || rec.identity[slot] < 0) continue;
    ctx.beginPath();
    let pen = false;
    for (let i = 0; i < path.length; i += 2) {
      if (Number.isNaN(path[i])) {
        pen = false;
        continue;
      }
      if (pen) ctx.lineTo(px(path[i]), py(path[i + 1]));
      else ctx.moveTo(px(path[i]), py(path[i + 1]));
      pen = true;
    }
    // A faint ink halo keeps pale identity colors readable on pale maps.
    ctx.globalAlpha = HALO.alpha;
    ctx.strokeStyle = INK;
    ctx.lineWidth = TAPESTRY.lineWidth + HALO.extra;
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = colors[rec.identity[slot]] ?? INK;
    ctx.lineWidth = TAPESTRY.lineWidth;
    ctx.stroke();
  }
  ctx.restore();
}

function drawSplash(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  const a = LAYOUT.splashArm;
  ctx.save();
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x - a, y - a);
  ctx.lineTo(x + a, y + a);
  ctx.moveTo(x + a, y - a);
  ctx.lineTo(x - a, y + a);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 9;
  ctx.stroke();
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 4;
  ctx.stroke();
  ctx.restore();
}

/** A three-point gold crown standing on (x, y), inked, with a sticker shadow and a jewel. */
function drawCrown(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  const w = LAYOUT.crownWidth;
  const h = w * 0.72;
  const l = x - w / 2;
  const top = y - h - 6;
  const base = y - 6;
  const path = (dy: number): void => {
    ctx.beginPath();
    ctx.moveTo(l, base + dy);
    ctx.lineTo(l, top + h * 0.25 + dy);
    ctx.lineTo(l + w * 0.25, top + h * 0.55 + dy);
    ctx.lineTo(x, top + dy);
    ctx.lineTo(l + w * 0.75, top + h * 0.55 + dy);
    ctx.lineTo(l + w, top + h * 0.25 + dy);
    ctx.lineTo(l + w, base + dy);
    ctx.closePath();
  };
  ctx.save();
  ctx.lineJoin = 'round';
  ctx.strokeStyle = INK;
  ctx.lineWidth = 5;
  path(5);
  ctx.stroke();
  path(0);
  ctx.fillStyle = GOLD;
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#FF5A4E';
  ctx.beginPath();
  ctx.arc(x, base - h * 0.3, w * 0.08, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawText(ctx: CanvasRenderingContext2D, rec: RoundRecord, info: TapestryInfo, colors: readonly string[]): void {
  const s = TAPESTRY.size;
  const round = rec.golden ? 'Golden Round' : `Round ${rec.roundIndex + 1}`;
  const date = info.date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  const title = `SHOVE OFF! — ${round} on ${MAPS[rec.mapId].name}`;
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.lineJoin = 'round';
  // Sticker title: a pink (gold in the Golden Round) offset print under the ink.
  ctx.font = '54px "Lilita One", sans-serif';
  ctx.fillStyle = rec.golden ? GOLD_DEEP : PINK;
  ctx.fillText(title, s / 2 + 3, LAYOUT.titleY + 4, s - 140);
  ctx.fillStyle = INK;
  ctx.fillText(title, s / 2, LAYOUT.titleY, s - 140);
  ctx.font = '800 26px "Nunito", sans-serif';
  ctx.fillText(`${date} • Room ${info.code}`, s / 2, LAYOUT.subtitleY);

  const top = info.standings.slice(0, 3);
  const c = LAYOUT.chip;
  const total = top.length * c.width + (top.length - 1) * c.gap;
  const x0 = (s - total) / 2;
  const cy = LAYOUT.standingsY;
  top.forEach((row, i) => {
    const x = x0 + i * (c.width + c.gap);
    const y = cy - c.height / 2;
    // A white sticker chip with an ink border and a hard drop shadow, tilted a hair.
    ctx.save();
    ctx.translate(x + c.width / 2, cy);
    ctx.rotate(((i - 1) * 1.2 * Math.PI) / 180);
    ctx.translate(-(x + c.width / 2), -cy);
    ctx.fillStyle = INK;
    roundRect(ctx, x, y + 6, c.width, c.height, c.radius);
    ctx.fill();
    ctx.fillStyle = CHIP;
    roundRect(ctx, x, y, c.width, c.height, c.radius);
    ctx.fill();
    ctx.strokeStyle = INK;
    ctx.lineWidth = 4;
    ctx.stroke();
    const mx = x + 50;
    ctx.fillStyle = MEDALS[i];
    ctx.beginPath();
    ctx.arc(mx, cy, 30, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = INK;
    ctx.textAlign = 'center';
    ctx.font = '24px "Lilita One", sans-serif';
    ctx.fillText(PLACES[i], mx, cy + 9);
    ctx.textAlign = 'left';
    ctx.font = '34px "Lilita One", sans-serif';
    ctx.fillStyle = colors[row.identity] ?? INK;
    ctx.strokeStyle = INK;
    ctx.lineWidth = 7;
    ctx.strokeText(row.name, x + 92, cy - 2, c.width - 108);
    ctx.fillText(row.name, x + 92, cy - 2, c.width - 108);
    ctx.fillStyle = INK;
    ctx.font = '800 22px "Nunito", sans-serif';
    ctx.fillText(`${row.score} pts`, x + 92, cy + 28);
    ctx.restore();
  });
  ctx.restore();
}
